import { useStatus } from '../../api/status';
import type { ProbeState, StatusGroup, StatusReport } from '../../api/types';
import { can, useMe } from '../../auth/AuthContext';
import { EmptyState, ErrorState, SkeletonStats } from '../../components/states';
import { Badge, PageHeader, type Tone } from '../../components/ui';
import { formatRelative } from '../../format';

/**
 * One card per service, up or down, nothing else: the page says whether the
 * hub's services are working, not how. The API's report is reduced to a
 * boolean per area here; hosts, ids, latencies and probe details are never
 * shown. The API and its database are covered by /healthz and are not listed.
 */
const SERVICES: Array<{ group: StatusGroup; label: string }> = [
  { group: 'email', label: 'Mail' },
  { group: 'whatsapp', label: 'WhatsApp' },
  { group: 'edge', label: 'Cloudflare' },
];

type ServiceState = 'up' | 'down' | 'off';

const STATE_COPY: Record<ServiceState, { tone: Tone; label: string }> = {
  up: { tone: 'ok', label: 'Up' },
  down: { tone: 'bad', label: 'Down' },
  off: { tone: 'neutral', label: 'Not set up' },
};

/** A service is up when every probe in its group passed; a warning counts as up. */
export function serviceState(report: StatusReport, group: StatusGroup): ServiceState {
  const states: ProbeState[] = report.checks.filter((c) => c.group === group).map((c) => c.state);
  const live = states.filter((s) => s !== 'off');
  if (live.length === 0) return 'off';
  return live.some((s) => s === 'fail') ? 'down' : 'up';
}

export function Status() {
  const me = useMe();
  const allowed = can(me, 'status:read');
  const status = useStatus({ enabled: allowed });

  const header = <PageHeader title="Status" />;

  if (!allowed) {
    return (
      <>
        {header}
        <EmptyState title="Admins only" body="This page needs the status:read permission." />
      </>
    );
  }
  if (status.isPending) {
    return (
      <>
        {header}
        <SkeletonStats count={SERVICES.length} />
      </>
    );
  }
  if (status.isError) {
    return (
      <>
        {header}
        <ErrorState
          error={status.error}
          onRetry={() => status.refetch()}
          retrying={status.isFetching}
        />
      </>
    );
  }

  const report = status.data;
  const services = SERVICES.map((s) => ({ ...s, state: serviceState(report, s.group) }));
  const down = services.filter((s) => s.state === 'down');

  return (
    <>
      {header}
      <p className="status-summary">
        {down.length === 0 ? (
          <Badge tone="ok">All services up</Badge>
        ) : (
          <Badge tone="bad">
            {down.length === 1 ? `${down[0]!.label} is down` : `${down.length} services down`}
          </Badge>
        )}{' '}
        <span className="muted">
          Checked <time dateTime={report.checkedAt}>{formatRelative(report.checkedAt)}</time>
        </span>
      </p>
      <ul className="service-cards">
        {services.map((s) => (
          <li key={s.group} className="service-card" data-state={s.state}>
            <span className="service-card__name">{s.label}</span>
            <Badge tone={STATE_COPY[s.state].tone}>{STATE_COPY[s.state].label}</Badge>
          </li>
        ))}
      </ul>
    </>
  );
}
